/**
 * Authorised Domains Section
 * 
 * Admin UI for managing authorised email domains for a tenancy.
 * Allows admins to:
 * - Add and verify domains
 * - Configure join policies (AUTO_JOIN, APPROVAL_REQUIRED, INVITE_ONLY)
 * - Configure auth modes (SSO_ONLY, PREFER_SSO, ANY)
 * - Link SSO providers to domains
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTenancy } from '@/contexts/TenancyContext';
import { useCurrentUser } from '@/contexts/TenancyContext';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Globe, 
  Shield, 
  Check, 
  X, 
  RefreshCw, 
  Plus, 
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Trash2,
  Copy,
  Pencil
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, 
  faShieldCheck, 
  faXmark, 
  faUserShield, 
  faKey 
} from '@awesome.me/kit-269fbef2e2/icons/duotone/solid';
import { toast } from 'sonner';
import {
  AuthorisedDomain,
  SSOProvider,
  JoinPolicy,
  AuthMode,
  DefaultRole,
  AuthorisedDomainFormData,
} from '@/types/authorisedDomains';
import {
  getAuthorisedDomains,
  createAuthorisedDomain,
  updateAuthorisedDomain,
  deleteAuthorisedDomain,
  initiateVerification,
  checkVerification,
  getSSOProviders,
} from '@/services/authDomainService';

const JOIN_POLICY_OPTIONS: { value: JoinPolicy; label: string; description: string }[] = [
  { 
    value: 'AUTO_JOIN', 
    label: 'Auto Join', 
    description: 'Users are automatically added to your organization' 
  },
  { 
    value: 'APPROVAL_REQUIRED', 
    label: 'Approval Required', 
    description: 'Admins must approve join requests' 
  },
  { 
    value: 'INVITE_ONLY', 
    label: 'Invite Only', 
    description: 'Users can only join with an invitation' 
  },
];

const AUTH_MODE_OPTIONS: { value: AuthMode; label: string; description: string }[] = [
  { 
    value: 'ANY', 
    label: 'Any Method', 
    description: 'Users can sign in with any available method' 
  },
  { 
    value: 'PREFER_SSO', 
    label: 'Prefer SSO', 
    description: 'SSO is shown first, but other methods are available' 
  },
  { 
    value: 'SSO_ONLY', 
    label: 'SSO Only', 
    description: 'Users must sign in with company SSO' 
  },
];

const DEFAULT_ROLE_OPTIONS: { value: DefaultRole; label: string; description: string }[] = [
  { 
    value: 'POLICY_READER', 
    label: 'Policy Reader', 
    description: 'Read-only access to policies and documents' 
  },
  { 
    value: 'PLATFORM_USER', 
    label: 'Platform User', 
    description: 'Full platform access with owner permissions' 
  },
];

export function AuthorisedDomainsSection() {
  const { activeTenancy } = useTenancy();
  const currentUser = useCurrentUser();
  const tenancyId = activeTenancy?.id;
  const tenancyName = activeTenancy?.name || '';

  const [domains, setDomains] = useState<AuthorisedDomain[]>([]);
  const [ssoProviders, setSSOProviders] = useState<SSOProvider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<AuthorisedDomain | null>(null);
  const [editingDomain, setEditingDomain] = useState<AuthorisedDomain | null>(null);
  const [verificationInfo, setVerificationInfo] = useState<{
    txtRecordName: string;
    txtRecordValue: string;
  } | null>(null);

  // Form state for adding a new domain
  const [formData, setFormData] = useState<AuthorisedDomainFormData>({
    domain: '',
    joinPolicy: 'APPROVAL_REQUIRED',
    authMode: 'ANY',
    defaultRole: 'POLICY_READER',
    ssoProviderId: undefined,
  });

  // Form state for editing a domain
  const [editFormData, setEditFormData] = useState<Omit<AuthorisedDomainFormData, 'domain'>>({
    joinPolicy: 'APPROVAL_REQUIRED',
    authMode: 'ANY',
    defaultRole: 'POLICY_READER',
    ssoProviderId: undefined,
  });

  const loadData = useCallback(async () => {
    if (!tenancyId) return;

    setIsLoading(true);
    try {
      const [domainsData, providersData] = await Promise.all([
        getAuthorisedDomains(tenancyId),
        getSSOProviders(tenancyId),
      ]);
      setDomains(domainsData);
      setSSOProviders(providersData);
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error loading data:', error);
      toast.error('Failed to load authorised domains');
    } finally {
      setIsLoading(false);
    }
  }, [tenancyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddDomain = async () => {
    if (!tenancyId || !currentUser?.userID) return;

    if (!formData.domain) {
      toast.error('Please enter a domain');
      return;
    }

    try {
      const result = await createAuthorisedDomain(
        tenancyId,
        tenancyName,
        formData,
        currentUser.userID
      );

      if (result.success && result.domain) {
        setDomains(prev => [result.domain!, ...prev]);
        setIsAddDialogOpen(false);
        setFormData({
          domain: '',
          joinPolicy: 'APPROVAL_REQUIRED',
          authMode: 'ANY',
          defaultRole: 'POLICY_READER',
          ssoProviderId: undefined,
        });
        toast.success('Domain added. Please verify ownership.');
        
        // Open verification dialog
        setSelectedDomain(result.domain);
        await handleInitiateVerification(result.domain);
      } else {
        toast.error(result.error || 'Failed to add domain');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error adding domain:', error);
      toast.error('Failed to add domain');
    }
  };

  const handleInitiateVerification = async (domain: AuthorisedDomain) => {
    if (!tenancyId) return;

    try {
      const result = await initiateVerification(tenancyId, domain.id);
      
      if (result.success && result.txtRecordName && result.txtRecordValue) {
        setVerificationInfo({
          txtRecordName: result.txtRecordName,
          txtRecordValue: result.txtRecordValue,
        });
        setSelectedDomain(domain);
        setIsVerifyDialogOpen(true);
      } else {
        toast.error(result.error || 'Failed to initiate verification');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error initiating verification:', error);
      toast.error('Failed to initiate verification');
    }
  };

  const handleCheckVerification = async () => {
    if (!tenancyId || !selectedDomain) return;

    try {
      const result = await checkVerification(tenancyId, selectedDomain.id);
      
      if (result.verified) {
        toast.success('Domain verified successfully!');
        setIsVerifyDialogOpen(false);
        loadData(); // Reload to get updated status
      } else {
        toast.error(result.error || 'Verification failed. Please check your DNS records.');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error checking verification:', error);
      toast.error('Failed to check verification');
    }
  };

  const handleDeleteDomain = async (domain: AuthorisedDomain) => {
    if (!tenancyId) return;

    if (!confirm(`Are you sure you want to delete ${domain.domain}?`)) {
      return;
    }

    try {
      const result = await deleteAuthorisedDomain(tenancyId, domain.id);
      
      if (result.success) {
        setDomains(prev => prev.filter(d => d.id !== domain.id));
        toast.success('Domain deleted');
      } else {
        toast.error(result.error || 'Failed to delete domain');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error deleting domain:', error);
      toast.error('Failed to delete domain');
    }
  };

  const handleUpdateDomain = async (
    domainId: string,
    updates: Partial<AuthorisedDomainFormData>
  ) => {
    if (!tenancyId || !currentUser?.userID) return;

    try {
      const result = await updateAuthorisedDomain(
        tenancyId,
        domainId,
        updates,
        currentUser.userID
      );

      if (result.success) {
        setDomains(prev => prev.map(d => 
          d.id === domainId ? { ...d, ...updates } : d
        ));
        toast.success('Domain updated');
      } else {
        toast.error(result.error || 'Failed to update domain');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error updating domain:', error);
      toast.error('Failed to update domain');
    }
  };

  const handleOpenEditDialog = (domain: AuthorisedDomain) => {
    setEditingDomain(domain);
    setEditFormData({
      joinPolicy: domain.joinPolicy,
      authMode: domain.authMode,
      defaultRole: domain.defaultRole || 'POLICY_READER',
      ssoProviderId: domain.ssoProviderId,
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingDomain || !tenancyId || !currentUser?.userID) return;

    try {
      const result = await updateAuthorisedDomain(
        tenancyId,
        editingDomain.id,
        editFormData,
        currentUser.userID
      );

      if (result.success) {
        setDomains(prev => prev.map(d => 
          d.id === editingDomain.id ? { ...d, ...editFormData } : d
        ));
        setIsEditDialogOpen(false);
        setEditingDomain(null);
        toast.success('Domain updated');
      } else {
        toast.error(result.error || 'Failed to update domain');
      }
    } catch (error) {
      console.error('[AuthorisedDomainsSection] Error updating domain:', error);
      toast.error('Failed to update domain');
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getVerificationStatusBadge = (domain: AuthorisedDomain) => {
    if (domain.verified) {
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Verified
        </Badge>
      );
    }
    
    switch (domain.verificationStatus) {
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            <AlertTriangle className="mr-1 h-3 w-3" />
            Unverified
          </Badge>
        );
    }
  };

  const getJoinPolicyBadge = (policy: JoinPolicy) => {
    switch (policy) {
      case 'AUTO_JOIN':
        return <Badge variant="default">Auto Join</Badge>;
      case 'APPROVAL_REQUIRED':
        return <Badge variant="secondary">Approval Required</Badge>;
      case 'INVITE_ONLY':
        return <Badge variant="outline">Invite Only</Badge>;
    }
  };

  const getAuthModeBadge = (mode: AuthMode) => {
    switch (mode) {
      case 'SSO_ONLY':
        return <Badge className="bg-blue-500">SSO Only</Badge>;
      case 'PREFER_SSO':
        return <Badge variant="secondary">Prefer SSO</Badge>;
      case 'ANY':
        return <Badge variant="outline">Any Method</Badge>;
    }
  };

  if (!tenancyId) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Authorised Domains
            </CardTitle>
            <CardDescription>
              Configure which email domains can join your organization and how they authenticate.
            </CardDescription>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Domain
              </Button>
            </DialogTrigger>
            <DialogContent 
              className="max-w-2xl! w-2xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
                {/* Close button */}
                <button
                  onClick={() => setIsAddDialogOpen(false)}
                  className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  aria-label="Close"
                >
                  <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
                </button>
                
                {/* Header */}
                <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-primary/10 to-primary/30 mb-4">
                    <FontAwesomeIcon icon={faGlobe} className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Add Authorised Domain</h2>
                  <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                    Add a domain that users can use to sign up for your organization.
                  </p>
                </div>
                
                {/* Form Content */}
                <div className="p-8 flex-1 min-h-0 overflow-y-auto space-y-6">
                {/* Domain */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-primary/10 to-primary/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faGlobe} className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label htmlFor="domain" className="block text-sm font-semibold text-foreground mb-1">
                        Domain <span className="text-destructive">*</span>
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Enter the domain without @ or protocol (e.g., "acme.com")
                      </p>
                      <Input
                        id="domain"
                        placeholder="example.com"
                        value={formData.domain}
                        onChange={(e) => setFormData(prev => ({ 
                          ...prev, 
                          domain: e.target.value.toLowerCase().trim() 
                        }))}
                        className="bg-white/5 border border-primary/10 text-base h-11 focus:border-primary/50"
                      />
                    </div>
                  </div>
                </div>

                {/* Join Policy */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-emerald-500/10 to-emerald-500/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faUserShield} className="w-6 h-6 text-emerald-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Join Policy
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        How users with this domain can join your organization
                      </p>
                      <Select
                        value={formData.joinPolicy}
                        onValueChange={(value: JoinPolicy) => 
                          setFormData(prev => ({ ...prev, joinPolicy: value }))
                        }
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="w-full min-w-[400px]">
                          {JOIN_POLICY_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                              <div className="flex flex-col gap-1">
                                <div className="font-medium text-sm leading-none">{option.label}</div>
                                <div className="text-xs text-muted-foreground leading-tight">
                                  {option.description}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Authentication Mode */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-blue-500/10 to-blue-500/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldCheck} className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Authentication Mode
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        How users authenticate when signing in
                      </p>
                      <Select
                        value={formData.authMode}
                        onValueChange={(value: AuthMode) => 
                          setFormData(prev => ({ ...prev, authMode: value }))
                        }
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="w-full min-w-[400px]">
                          {AUTH_MODE_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                              <div className="flex flex-col gap-1">
                                <div className="font-medium text-sm leading-none">{option.label}</div>
                                <div className="text-xs text-muted-foreground leading-tight">
                                  {option.description}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Default Role */}
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-purple-500/10 to-purple-500/30 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-purple-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        Default Role
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        The role assigned to users when they join via this domain
                      </p>
                      <Select
                        value={formData.defaultRole}
                        onValueChange={(value: DefaultRole) => 
                          setFormData(prev => ({ ...prev, defaultRole: value }))
                        }
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="w-full min-w-[400px]">
                          {DEFAULT_ROLE_OPTIONS.map(option => (
                            <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                              <div className="flex flex-col gap-1">
                                <div className="font-medium text-sm leading-none">{option.label}</div>
                                <div className="text-xs text-muted-foreground leading-tight">
                                  {option.description}
                                </div>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* SSO Provider */}
                {ssoProviders.length > 0 && (
                  <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5 transition-all hover:border-primary/30 hover:bg-white/[0.07]">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-amber-500/10 to-amber-500/30 flex items-center justify-center">
                        <FontAwesomeIcon icon={faKey} className="w-6 h-6 text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label className="block text-sm font-semibold text-foreground mb-1">
                          SSO Provider <span className="text-muted-foreground text-xs">(optional)</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Select an SSO provider for this domain
                        </p>
                        <Select
                          value={formData.ssoProviderId || 'none'}
                          onValueChange={(value) => {
                            const provider = ssoProviders.find(p => p.id === value);
                            setFormData(prev => ({ 
                              ...prev, 
                              ssoProviderId: value === 'none' ? null : value,
                              firebaseProviderId: value === 'none' ? null : provider?.firebaseProviderId,
                            }));
                          }}
                        >
                          <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                            <SelectValue placeholder="No SSO" />
                          </SelectTrigger>
                          <SelectContent className="w-full min-w-[300px]">
                            <SelectItem value="none" className="cursor-pointer">No SSO</SelectItem>
                            {ssoProviders.filter(p => p.enabled).map(provider => (
                              <SelectItem key={provider.id} value={provider.id} className="cursor-pointer">
                                {provider.displayName || provider.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="border-t border-white/10 px-8 py-5 bg-card flex justify-between items-center shrink-0">
                <p className="text-xs text-muted-foreground">
                  {!formData.domain.trim() ? "Enter a domain to continue" : "Ready to add domain"}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddDomain}>
                    Add Domain
                  </Button>
                </div>
              </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : domains.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No authorised domains</h3>
            <p className="text-sm text-muted-foreground max-w-sm mt-2">
              Add a domain to allow users with matching email addresses to join your organization.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Join Policy</TableHead>
                <TableHead>Auth Mode</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {domains.map(domain => (
                <TableRow key={domain.id}>
                  <TableCell className="font-medium">
                    {domain.domain}
                  </TableCell>
                  <TableCell>
                    {getVerificationStatusBadge(domain)}
                  </TableCell>
                  <TableCell>
                    {getJoinPolicyBadge(domain.joinPolicy)}
                  </TableCell>
                  <TableCell>
                    {getAuthModeBadge(domain.authMode)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!domain.verified && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleInitiateVerification(domain)}
                        >
                          Verify
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEditDialog(domain)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteDomain(domain)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Verification Dialog */}
      <Dialog open={isVerifyDialogOpen} onOpenChange={setIsVerifyDialogOpen}>
        <DialogContent 
          className="sm:max-w-lg bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-card">
              <DialogHeader className="text-center">
                <DialogTitle className="text-2xl font-bold">Verify Domain Ownership</DialogTitle>
                <DialogDescription className="mt-2">
                  Add the following TXT record to your DNS to verify ownership of{' '}
                  <strong>{selectedDomain?.domain}</strong>
                </DialogDescription>
              </DialogHeader>
            </div>
            
            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto">
          
          {verificationInfo && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>TXT Record Name</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono">
                    {verificationInfo.txtRecordName}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(verificationInfo.txtRecordName, 'Record name')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>TXT Record Value</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono break-all">
                    {verificationInfo.txtRecordValue}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(verificationInfo.txtRecordValue, 'Record value')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border bg-muted/50 p-4">
                <h4 className="font-medium mb-2">Instructions</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Log in to your DNS provider (e.g., Cloudflare, Route53, GoDaddy)</li>
                  <li>Create a new TXT record with the name and value above</li>
                  <li>Wait for DNS propagation (can take up to 48 hours)</li>
                  <li>Click "Check Verification" below to verify</li>
                </ol>
              </div>
            </div>
          )}

          </div>
            
            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card">
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsVerifyDialogOpen(false)}>
                  Close
                </Button>
                <Button onClick={handleCheckVerification}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check Verification
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Domain Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent 
          className="sm:max-w-2xl bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-white/10 bg-card">
              <DialogHeader className="text-center">
                <DialogTitle className="text-2xl font-bold">Edit Domain Settings</DialogTitle>
                <DialogDescription className="mt-2">
                  Update settings for <strong>{editingDomain?.domain}</strong>
                </DialogDescription>
              </DialogHeader>
            </div>
            
            {/* Content */}
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Join Policy */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-emerald-500/10 to-emerald-500/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faUserShield} className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Join Policy
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      How users with this domain can join your organization
                    </p>
                    <Select
                      value={editFormData.joinPolicy}
                      onValueChange={(value: JoinPolicy) => 
                        setEditFormData(prev => ({ ...prev, joinPolicy: value }))
                      }
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="w-full min-w-[400px]">
                        {JOIN_POLICY_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                            <div className="flex flex-col gap-1">
                              <div className="font-medium text-sm leading-none">{option.label}</div>
                              <div className="text-xs text-muted-foreground leading-tight">
                                {option.description}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Authentication Mode */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-blue-500/10 to-blue-500/30 flex items-center justify-center">
                    <FontAwesomeIcon icon={faShieldCheck} className="w-6 h-6 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Authentication Mode
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      How users authenticate when signing in
                    </p>
                    <Select
                      value={editFormData.authMode}
                      onValueChange={(value: AuthMode) => 
                        setEditFormData(prev => ({ ...prev, authMode: value }))
                      }
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="w-full min-w-[400px]">
                        {AUTH_MODE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                            <div className="flex flex-col gap-1">
                              <div className="font-medium text-sm leading-none">{option.label}</div>
                              <div className="text-xs text-muted-foreground leading-tight">
                                {option.description}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Default Role */}
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-purple-500/10 to-purple-500/30 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-purple-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Label className="block text-sm font-semibold text-foreground mb-1">
                      Default Role
                    </Label>
                    <p className="text-xs text-muted-foreground mb-3">
                      The role assigned to users when they join via this domain
                    </p>
                    <Select
                      value={editFormData.defaultRole}
                      onValueChange={(value: DefaultRole) => 
                        setEditFormData(prev => ({ ...prev, defaultRole: value }))
                      }
                    >
                      <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="w-full min-w-[400px]">
                        {DEFAULT_ROLE_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value} className="cursor-pointer py-3">
                            <div className="flex flex-col gap-1">
                              <div className="font-medium text-sm leading-none">{option.label}</div>
                              <div className="text-xs text-muted-foreground leading-tight">
                                {option.description}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* SSO Provider */}
              {ssoProviders.length > 0 && (
                <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-5">
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 w-12 h-12 rounded-lg bg-linear-to-br from-amber-500/10 to-amber-500/30 flex items-center justify-center">
                      <FontAwesomeIcon icon={faKey} className="w-6 h-6 text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="block text-sm font-semibold text-foreground mb-1">
                        SSO Provider <span className="text-muted-foreground text-xs">(optional)</span>
                      </Label>
                      <p className="text-xs text-muted-foreground mb-3">
                        Link this domain to a specific SSO provider
                      </p>
                      <Select
                        value={editFormData.ssoProviderId || 'none'}
                        onValueChange={(value) => {
                          const provider = ssoProviders.find(p => p.id === value);
                          setEditFormData(prev => ({ 
                            ...prev, 
                            ssoProviderId: value === 'none' ? null : value,
                            firebaseProviderId: value === 'none' ? null : provider?.firebaseProviderId,
                          }));
                        }}
                      >
                        <SelectTrigger className="bg-white/5 border-primary/10 h-11 w-full">
                          <SelectValue placeholder="Select SSO provider" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No SSO Provider</SelectItem>
                          {ssoProviders.map(provider => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="border-t border-white/10 px-6 py-4 bg-card flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                Changes will apply to new users joining via this domain
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default AuthorisedDomainsSection;
